import { submit, findByValue } from "../lib/Cart";
import { h, Fragment } from "preact";
import { useState, useRef, useEffect } from "preact/hooks";

const CARET =
  process.env.NODE_ENV === "development"
    ? "/img/icon-caret-down-dark.svg"
    : "/a/dist/img/icon-caret-down-dark.svg";
const CARET_ACTIVE =
  process.env.NODE_ENV === "development"
    ? "/img/icon-caret-down-light.svg"
    : "/a/dist/img/icon-caret-down-light.svg";
const PREV =
  process.env.NODE_ENV === "development"
    ? "/img/icon-pagination-prev.svg"
    : "/a/dist/img/icon-pagination-prev.svg";
const NEXT =
  process.env.NODE_ENV === "development"
    ? "/img/icon-pagination-next.svg"
    : "/a/dist/img/icon-pagination-next.svg";
const MINUS =
  process.env.NODE_ENV === "development"
    ? "/img/icon-minus.svg"
    : "/a/dist/img/icon-minus.svg";
const PLUS =
  process.env.NODE_ENV === "development"
    ? "/img/icon-plus.svg"
    : "/a/dist/img/icon-plus.svg";

function uniq(array) {
  const uniquedArray = [];
  for (const elem of array) {
    if (uniquedArray.indexOf(elem) < 0) uniquedArray.push(elem);
  }
  return uniquedArray;
}

function split(array, n) {
  return array.reduce(
    (acc, c, i) => (i % n ? acc : [...acc, [array.slice(i, i + n)]]),
    []
  );
}

function checkRange(items) {
  const item = items[0];
  if (!item) {
    return false;
  }

  for (let i = 6; i < item.length; i++) {
    if (/^\d+-\d+$/.test(item[i])) {
      return true;
    }
  }

  return false;
}

function formatDate(date) {
  const d = new Date(date);
  return d.getMonth() + 1 + "月" + d.getDate() + "日";
}

function grouping(items) {
  const groups = [];

  let group = { items: [] };
  items.forEach((item) => {
    if (group.items.length !== 0) {
      const match = group.commonHeaders.every((v, i) => item[i + 6] === v);
      if (match) {
        group.items.push(item);
      } else {
        groups.push(group);
        group = { items: [] };
      }
    }

    if (group.items.length === 0) {
      group.items.push(item);

      group.commonHeaders = [];
      for (let i = 6; i < item.length; i++) {
        if (/^\d+-\d+$/.test(item[i])) {
          break;
        }
        group.commonHeaders.push(item[i]);
      }
    }

    group.items.push(item);
  });
  groups.push(group);

  return groups.map((group) => {
    const g = [
      group.items[0][1] + "-" + group.items[group.items.length - 1][1],
      group.items[0][1] + "～" + group.items[group.items.length - 1][1],
      group.items[0][2],
      Math.min
        .apply(
          null,
          group.items.map((item) => item[3].replace(/\D/g, ""))
        )
        .toString(),
      Math.max.apply(
        null,
        group.items.map((item) => item[4])
      ),
      [
        Math.min.apply(
          null,
          group.items.map((item) => Date.parse(item[5]))
        ),
      ],
    ].concat(group.commonHeaders);
    g.name = g[0];
    g.commonHeaders = group.commonHeaders;
    g.items = group.items;

    return g;
  });
}

const Table = ({ perPage, addFavorites, goods, type }) => {
  const formElRef = useRef(null);
  const hasRange = checkRange(goods.items);

  let _ditems = [];
  if (hasRange) {
    _ditems = grouping(goods.items);
  } else {
    _ditems = goods.items.concat();
  }
  const [ditems] = useState(_ditems);

  const [page, _setPage] = useState(1);
  const setPage = (p) => {
    return (ev) => {
      ev.preventDefault();
      _setPage(p);

      setTimeout(() => {
        const yOffset = -60;
        const y =
          formElRef.current.getBoundingClientRect().top +
          window.pageYOffset +
          yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }, 100);
    };
  };

  const [filter, _setFilter] = useState({});
  const setFilter = (k, v) => {
    return (ev) => {
      ev.preventDefault();
      if (v) {
        filter[k] = v;
      } else {
        delete filter[k];
      }
      _setFilter(Object.assign({}, filter));
      _setPage(1);
    };
  };

  const [numberInputs, _setNumberInputs] = useState({});
  const [numbers, _setNumber] = useState({});
  const setNumber = (code, diff) => {
    return (ev) => {
      ev.preventDefault();
      const target = ev.currentTarget;

      const number = diff
        ? (numbers[code] || 0) + diff
        : parseInt(target.value, 10);
      const normalized =
        target instanceof HTMLSelectElement
          ? number
          : Math.max(
              target.dataset.min || target.getAttribute("min"),
              Math.min(target.dataset.max || target.getAttribute("max"), number)
            );

      if (
        normalized >= 10 &&
        (target instanceof HTMLSelectElement
          ? target.textContent.endsWith("＋")
          : true)
      ) {
        _setNumberInputs(Object.assign({}, numberInputs, { [code]: true }));
      }
      _setNumber(Object.assign({}, numbers, { [code]: normalized }));
    };
  };

  const [remarks, _setRemark] = useState({});
  const setRemark = (code, index) => {
    return (ev) => {
      ev.preventDefault();

      const value = ev.target.value ? parseInt(ev.target.value) : "";
      const set = remarks[code] || [];
      set[index] = value;

      _setRemark(Object.assign({}, remarks, { [code]: set }));
    };
  };

  const [, _resolve] = useState(0);
  const validateRemarks = (code) => {
    return new Promise((resolve) => {
      ditems.forEach((item) => {
        if (item[0] !== code) {
          return;
        }

        item.items.forEach((it) => {
          let lastValue = null;
          for (let i = 6 + item.commonHeaders.length; i < it.length; i++) {
            if (!remarks[code] || !remarks[code][i]) {
              throw goods.headers[i - 6] + "を指定してください。";
            }

            const v = parseInt(remarks[code][i]);
            if (lastValue && lastValue < v) {
              throw (
                goods.headers[i - 6] +
                "には" +
                goods.headers[i - 6 - 1] +
                "以下の数値を指定してください"
              );
            }
            lastValue = v;
          }
        });
      });

      resolve();
    });
  };
  const resolve = (code) => {
    return (ev) => {
      ev.preventDefault();

      let item;
      ditems.forEach((_i) => {
        if (_i[0] !== code) {
          return;
        }
        item = _i;
      });

      if (!item) {
        return;
      }

      validateRemarks(code)
        .then(() => findByValue(formElRef.current, code))
        .then((data) => {
          if (data.error) {
            throw data.error;
          }

          item.items.forEach((_i) => {
            if (data.item.code === _i[1]) {
              [1, 3, 5].forEach((i) => {
                item[i] = _i[i];
              });
            }
          });

          _resolve(Math.random());
        })
        .catch((err) => {
          alert(err);
        });
    };
  };
  const validateThenSubmit = (ev) => {
    const form = ev.target;
    ev.preventDefault();

    let promise = Promise.resolve();
    for (let code in numbers) {
      if (!numbers[code]) {
        continue;
      }

      promise = promise
        .then(() => validateRemarks(code))
        .then(() => findByValue(formElRef.current, code))
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
        });
    }

    promise
      .then(() => submit(ev, form, true))
      .catch((err) => {
        alert(err);
      });
  };

  const filters = [];
  for (let i = 0; i < goods.headers.length; i++) {
    filters[i] = [];
  }
  if (type === "sm") {
    ditems.forEach((item) => {
      for (let i = 0; i < goods.headers.length; i++) {
        filters[i].push(item[i + 6]);
      }
    });
  }

  const items = [];
  const itemsWithoutFilter = {};
  ditems.forEach((item) => {
    if (Object.keys(filter).every((k) => item[parseInt(k) + 6] === filter[k])) {
      items.push(item);
    }
    Object.keys(filter).forEach((i) => {
      itemsWithoutFilter[i] = itemsWithoutFilter[i] || [];
      const keys = Object.keys(filter).filter((j) => i !== j);
      if (keys.every((k) => item[parseInt(k) + 6] === filter[k])) {
        itemsWithoutFilter[i].push(item);
      }
    });
  });

  if (type === "md") {
    for (let i = 0; i < goods.headers.length; i++) {
      (itemsWithoutFilter[i] || items).forEach((item) => {
        filters[i].push(item[i + 6]);
      });
    }
  }

  for (let i = 0; i < goods.headers.length; i++) {
    filters[i] = uniq(filters[i]);
  }

  const total = items.length;
  const pages = Math.ceil(total / perPage);
  const start = (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);

  const pageList = [];
  for (let i = 1; i <= pages; i++) {
    pageList.push(i);
  }
  const skipedPageList = [];
  pageList.forEach((i) => {
    let show = false;
    const offset = page <= 3 ? 4 - page : 1;
    if (i === 1) {
      show = true;
    } else if (i === pageList[pageList.length - 1]) {
      show = true;
    } else if (i >= page - offset && i <= page + offset) {
      show = true;
    }

    if (!show) {
      return;
    }

    if (
      skipedPageList.length !== 0 &&
      skipedPageList[skipedPageList.length - 1] + 1 !== i
    ) {
      skipedPageList.push(0);
    }
    if (
      skipedPageList.length !== 0 &&
      skipedPageList[skipedPageList.length - 2] + 2 === i
    ) {
      skipedPageList[skipedPageList.length - 1] = i - 1;
    }
    skipedPageList.push(i);
  });

  const currentItems = items.slice(start - 1, end);

  useEffect(() => {
    jQuery(window).triggerHandler("resize.om.goods-item-table");
  });

  if (type === "md") {
    return (
      <form
        onSubmit={hasRange ? validateThenSubmit : submit}
        ref={formElRef}
        data-om-modal
      >
        <div className="row">
          <div className="col fs-12">
            {items.length} 件中 {start}-{end} 件表示
          </div>
        </div>
        <table className="mt-30">
          <thead>
            <tr>
              <th className="text-left">商品コード</th>
              <th>品番・品名</th>
              {goods.headers.map((hdr) => (
                <th
                  dangerouslySetInnerHTML={{
                    __html: hdr.replace("</CENTER>", ""),
                  }}
                ></th>
              ))}
              {hasRange && <th></th>}
              <th>税込価格</th>
              <th>数量</th>
              <th>出荷予定日</th>
            </tr>
            {(Object.keys(filter).length !== 0 ||
              [...filters.keys()].some((i) => filters[i].length > 1)) && (
              <tr>
                <th></th>
                <th></th>
                {[...filters.keys()].map((i) => {
                  if (!filter[i] && filters[i].length <= 1) {
                    return <th></th>;
                  } else {
                    return (
                      <th>
                        <div
                          className="dropdown-hover-container"
                          key={filter[i] || i}
                        >
                          <div className="dropdown-hover">
                            <button
                              type="button"
                              className={`btn btn-filter ${
                                filter[i] ? " btn-filter--active" : ""
                              }`}
                              data-toggle="dropdown"
                              aria-haspopup="true"
                              aria-expanded="false"
                            >
                              <img src={filter[i] ? CARET_ACTIVE : CARET} />
                            </button>
                            <div className="dropdown-menu dropdown-menu--filter">
                              {filter[i] && (
                                <a
                                  className="dropdown-item"
                                  href="#"
                                  onClick={setFilter(i, null)}
                                >
                                  選択解除
                                </a>
                              )}
                              {filters[i].map((f) => (
                                <a
                                  className={`dropdown-item ${
                                    filter[i] === f
                                      ? " dropdown-item--active"
                                      : ""
                                  }`}
                                  href="#"
                                  onClick={setFilter(i, f)}
                                >
                                  {f}
                                </a>
                              ))}
                            </div>
                          </div>
                        </div>
                      </th>
                    );
                  }
                })}
                <th></th>
                {hasRange && <th></th>}
                <th></th>
                <th></th>
              </tr>
            )}
          </thead>
          <tbody>
            {currentItems.map((item) => (
              <tr>
                <td className="text-left">{item[1]}</td>
                <td>{item[2]}</td>
                {[...filters.keys()].map((i) => {
                  if (typeof item[i + 6] === "undefined") {
                    return (
                      <td>
                        <input
                          type="number"
                          value={
                            remarks[item[0]] ? remarks[item[0]][i + 6] : ""
                          }
                          name={`cart[${item.name || item[1]}][remarks][${
                            goods.headers[i]
                          }]`}
                          onInput={setRemark(item[0], i + 6)}
                        />
                        <span> mm</span>
                      </td>
                    );
                  } else {
                    return (
                      <td>
                        {hasRange && (
                          <input
                            type="hidden"
                            name={`cart[${item.name || item[1]}][remarks][${
                              goods.headers[i]
                            }]`}
                            value={item[i + 6]}
                          />
                        )}
                        {item[i + 6]}
                      </td>
                    );
                  }
                })}
                {hasRange && (
                  <td>
                    <a href="#" class="text-body" onClick={resolve(item[0])}>
                      価格計算
                    </a>
                  </td>
                )}
                <td>
                  <span className="fw-600">{item[3]}</span>
                  <span> 円</span>
                  {hasRange && <span>～</span>}
                </td>
                <td style={{ height: "54px", width: "72px" }}>
                  {!numberInputs[item.name || item[1]] ? (
                    <div
                      className="border--input"
                      style={{ padding: "4.5px 10px" }}
                    >
                      <div className="pulldown-default pulldown-default--inline-num fs-14">
                        <select
                          className="form-control shadow-none"
                          name={`cart[${item.name || item[1]}][number]`}
                          value={numbers[item.name || item[1]] || 0}
                          onChange={setNumber(item.name || item[1])}
                        >
                          {[
                            ...Array(
                              Math.min(item[4] + 1, item[4] == 10 ? 11 : 10)
                            ),
                          ].map((_, i) => (
                            <option value={i}>{i}</option>
                          ))}
                          {item[4] > 10 && <option value="10">10＋</option>}
                        </select>
                      </div>
                    </div>
                  ) : (
                    <input
                      type="number"
                      name={`cart[${item.name || item[1]}][number]`}
                      min="0"
                      max={item[4]}
                      value={numbers[item.name || item[1]] || 0}
                      onFocus={(ev) => {
                        if (ev.target.value === "0") {
                          ev.target.value = "";
                        }
                      }}
                      onBlur={(ev) => {
                        if (ev.target.value === "") {
                          ev.target.value = "0";
                        }
                      }}
                      onInput={setNumber(item.name || item[1])}
                    />
                  )}
                  {hasRange && (
                    <input
                      type="hidden"
                      name={`cart[${item.name || item[1]}][find_by_value]`}
                      value="1"
                    />
                  )}
                </td>
                <td>
                  {typeof item[5] == "object"
                    ? formatDate(item[5][0]) + "～"
                    : formatDate(item[5])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="row mt-30 justify-content-end">
          <div className="col-auto text-right">
            <div className="form-group form-group-buttons">
              <a
                href={addFavorites}
                className="btn btn-default btn-default--watch-list"
              >
                マイカタログに入れる
              </a>
              <button type="submit" className="btn btn-primary">
                カートに追加
              </button>
            </div>
            <span className="fs-12 lh-18">
              返品・交換・キャンセルなどの保証につきましては、
              <a href="/guide/guarantee.php#guarantee3">こちら</a>
              をご覧ください。
            </span>
          </div>
        </div>
        <div className="row mt-50">
          <div className="col d-flex justify-content-center">
            <a
              href="/cart"
              className="btn btn-primary lh-40 width-240 d-flex align-items-center justify-content-center"
            >
              <img src="/a/dist/img/icon-cart.svg" alt="" class="mr-10" />
              カートを見る
            </a>
          </div>
        </div>

        {pages > 1 && (
          <div className="row mt-40">
            <div className="col col-pagination">
              {page > 1 && (
                <a
                  href="#"
                  className="btn btn-pagination"
                  onClick={setPage(page - 1)}
                >
                  <img src={PREV} />
                </a>
              )}
              {skipedPageList.map((p) =>
                p === 0 ? (
                  <span>…</span>
                ) : (
                  <a
                    href="#"
                    className={`btn btn-pagination ${
                      p === page ? "btn-pagination--dark" : ""
                    }`}
                    onClick={setPage(p)}
                  >
                    {p}
                  </a>
                )
              )}
              {page < pages && (
                <a
                  href="#"
                  className="btn btn-pagination"
                  onClick={setPage(page + 1)}
                >
                  <img src={NEXT} />
                </a>
              )}
            </div>
          </div>
        )}
      </form>
    );
  } else {
    return (
      <>
        <form
          onSubmit={hasRange ? validateThenSubmit : submit}
          ref={formElRef}
          data-om-modal
        >
          <div className="row">
            <div className="col fs-12">
              {items.length} 件中 {start}-{end} 件表示
            </div>
          </div>

          <div class="goods-item-table-wrap">
            {currentItems.map((item) => (
              <table className="mt-30">
                <tbody>
                  <tr>
                    <td>商品コード：{item[1]}</td>
                    <td>品番・品名：{item[2]}</td>
                  </tr>
                  {split(
                    [...filters.keys()].concat(hasRange ? [9999] : []),
                    2
                  ).map((data, o) => (
                    <tr key={o}>
                      {[0, 1]
                        .map((k) => data[0][k])
                        .map((i) => {
                          const k = i || 999;
                          if (i === undefined) {
                            return <td key={k}></td>;
                          }

                          if (i === 9999) {
                            return (
                              <td key={k}>
                                <button
                                  className="btn btn-text d-flex align-items-center p-0 border-0"
                                  onClick={resolve(item[0])}
                                >
                                  <img src="/a/dist/img/icon-refresh.svg" />
                                  <span style="width: 1ch"></span>
                                  <span>価格計算</span>
                                </button>
                              </td>
                            );
                          }

                          if (typeof item[i + 6] !== "undefined") {
                            return (
                              <td key={k}>
                                {hasRange && (
                                  <input
                                    type="hidden"
                                    name={`cart[${
                                      item.name || item[1]
                                    }][remarks][${goods.headers[i]}]`}
                                    value={item[i + 6]}
                                  />
                                )}
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: goods.headers[i].replace(
                                      "</CENTER>",
                                      ""
                                    ),
                                  }}
                                ></span>
                                ：{item[i + 6]}
                              </td>
                            );
                          }

                          return (
                            <td key={k}>
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: goods.headers[i].replace(
                                    "</CENTER>",
                                    ""
                                  ),
                                }}
                              ></span>
                              ：
                              <input
                                type="number"
                                value={
                                  remarks[item[0]]
                                    ? remarks[item[0]][i + 6]
                                    : ""
                                }
                                name={`cart[${item.name || item[1]}][remarks][${
                                  goods.headers[i]
                                }]`}
                                onInput={setRemark(item[0], i + 6)}
                                className="remarks-input"
                              />
                              <span> mm</span>
                            </td>
                          );
                        })}
                    </tr>
                  ))}
                  <tr>
                    <td>
                      <span>税込価格 </span>
                      <span className="fw-600">{item[3]}</span>
                      <span> 円</span>
                      {typeof item[5] == "object" && <span>～</span>}
                    </td>
                    <td>
                      出荷予定日：
                      {typeof item[5] == "object"
                        ? formatDate(item[5][0]) + "～"
                        : formatDate(item[5])}
                    </td>
                  </tr>
                  <tr>
                    <td className="position-relative">
                      <button
                        className="number-control"
                        onClick={setNumber(item.name || item[1], -1)}
                        data-min="0"
                        data-max={item[4]}
                      >
                        <img src={MINUS} alt="減らす" />
                      </button>
                      <div className="number">
                        <span className="fs-12">
                          数量：
                          <input
                            type="number"
                            name={`cart[${item.name || item[1]}][number]`}
                            min="0"
                            max={item[4]}
                            className="adjust-input-content number-input"
                            value={numbers[item.name || item[1]] || 0}
                            style={{
                              width:
                                ((numbers[item.name || item[1]] || 0).toString()
                                  .length || 1) + "ch",
                            }}
                            onFocus={(ev) => {
                              if (ev.target.value === "0") {
                                ev.target.value = "";
                              }
                            }}
                            onBlur={(ev) => {
                              if (ev.target.value === "") {
                                ev.target.value = "0";
                              }
                            }}
                            onInput={setNumber(item.name || item[1])}
                          />
                          {hasRange && (
                            <input
                              type="hidden"
                              name={`cart[${
                                item.name || item[1]
                              }][find_by_value]`}
                              value="1"
                            />
                          )}
                        </span>
                      </div>
                      <button
                        className="number-control"
                        onClick={setNumber(item.name || item[1], 1)}
                        data-min="0"
                        data-max={item[4]}
                      >
                        <img src={PLUS} alt="増やす" />
                      </button>
                    </td>
                    <td>
                      <button className="btn btn-primary">カートに追加</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            ))}
          </div>

          <div className="mt-20 fs-12 lh-18">
            返品・交換・キャンセルなどの保証につきましては、
            <a href="/guide/guarantee.php#guarantee3">こちら</a>
            をご覧ください。
          </div>

          {pages > 1 && (
            <div className="row mt-40">
              <div className="col col-pagination">
                {page > 1 && (
                  <a
                    href="#"
                    className="btn btn-pagination"
                    onClick={setPage(page - 1)}
                  >
                    <img src={PREV} />
                  </a>
                )}
                {skipedPageList.map((p) =>
                  p === 0 ? (
                    <a
                      href="#"
                      className="btn btn-pagination"
                      onClick={(ev) => ev.preventDefault()}
                    >
                      …
                    </a>
                  ) : (
                    <a
                      href="#"
                      className={`btn btn-pagination ${
                        p === page ? "btn-pagination--dark" : ""
                      }`}
                      onClick={setPage(p)}
                    >
                      {p}
                    </a>
                  )
                )}
                {page < pages && (
                  <a
                    href="#"
                    className="btn btn-pagination"
                    onClick={setPage(page + 1)}
                  >
                    <img src={NEXT} />
                  </a>
                )}
              </div>
            </div>
          )}

          <div className="row mt-30">
            {(Object.keys(filter).length !== 0 ||
              [...filters.keys()].some((i) => filters[i].length > 1)) && (
              <div className="col-12 text-right">
                <div className="form-group form-group-buttons">
                  <a
                    href="#"
                    className="btn btn-default"
                    onClick={(ev) => {
                      ev.preventDefault();
                      jQuery("#filterModal").modal("show");
                    }}
                  >
                    商品を絞り込む
                  </a>
                </div>
              </div>
            )}
            <div className="col-12 text-right">
              <div className="form-group form-group-buttons">
                <a
                  href={addFavorites}
                  className="btn btn-default btn-default--watch-list"
                >
                  マイカタログに入れる
                </a>
              </div>
            </div>

            <div className="col-12 text-right mb-20">
              <div className="d-inline-flex justify-content-center">
                <a
                  href="/cart"
                  className="btn btn-primary lh-38 d-flex align-items-center justify-content-center"
                  style={{ width: "223px" }}
                >
                  <img src="/a/dist/img/icon-cart.svg" alt="" class="mr-10" />
                  カートを見る
                </a>
              </div>
            </div>
          </div>
        </form>

        <div
          className="modal fade"
          id="filterModal"
          tabindex="-1"
          role="dialog"
          aria-labelledby="filterModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="filterModalLabel">
                  絞り込み条件
                </h5>
              </div>
              <div className="modal-body">
                {[...filters.keys()]
                  .filter((i) => filters[i].length > 1)
                  .map((i) => {
                    return (
                      <div key={filter[i] || i}>
                        <label
                          className="mt-24 mb-0"
                          dangerouslySetInnerHTML={{
                            __html: goods.headers[parseInt(i, 10)].replace(
                              "</CENTER>",
                              ""
                            ),
                          }}
                        ></label>

                        <div className="select mt-16">
                          <select
                            className="form-control width-md-240"
                            name={i}
                          >
                            <option>選択してください</option>
                            {filters[i].map((f) => (
                              <option value={f} selected={filter[i] === f}>
                                {f}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    );
                  })}
              </div>
              <div className="modal-footer form-group-buttons">
                <button
                  type="button"
                  className="btn btn-primary fs-12 w-100 m-0"
                  onClick={(ev) => {
                    ev.preventDefault();

                    const filter = {};
                    [...document.querySelectorAll("#filterModal select")]
                      .filter((s) => s.value)
                      .forEach((s) => {
                        filter[s.name] = s.value;
                      });

                    jQuery("#filterModal").modal("hide");
                    _setFilter(filter);
                    setPage(1)(ev);
                  }}
                >
                  この条件で検索
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
};

export { Table as default };
